import { useEffect, useRef } from 'react';

/** Assigned ids whose auto-save has already been dispatched.
 *
 * Module scope, not a ref, and that is the whole point of this hook. The
 * CellMeasurer wrapped around each row of the virtualized calendar list
 * remounts the row while it settles on a "just in time" height, and again
 * whenever the row scrolls out of the window and back. A ref-based guard
 * re-initializes on exactly those remounts, so it would let the table POST the
 * same exercise two or three times and create duplicate items in the database.
 *
 * A failed save deliberately stays in the set: the coach recovers by clicking
 * SAVE on the table that comes back, rather than the table re-POSTing on every
 * remeasure against an endpoint that is already failing.
 */
const autoSavedAssignedIds = new Set();

/** Whether this pre-save item's auto-save has already been dispatched. Lets a
 * table decide during its FIRST render whether it is the mount that saves — and
 * so whether to paint its loading state instead of the editable table. Deciding
 * this in an effect instead would flash the table for a frame. */
export const hasAutoSavedPreSaveItem = (assignedId) => autoSavedAssignedIds.has(assignedId);

/**
 * Saves a pre-save table once, on mount, for a freshly added item — so adding
 * an exercise assigns it immediately instead of leaving a placeholder behind a
 * SAVE button.
 *
 * The table owns the save itself; this only decides whether and when to fire
 * it. `onAutoSave` is expected to run the table's own
 * `handleSaveCalendarExercise`, which is the only thing that can assemble that
 * item type's submission object.
 *
 * @param {Object}   params
 * @param {string}   params.assignedId  the pre-save item's temporary assignedId
 * @param {boolean}  params.enabled     false opts this mount out entirely
 * @param {Function} params.onAutoSave  runs the table's save handler
 */
const usePreSaveAutoSave = ({ assignedId, enabled, onAutoSave }) => {
  /** Read through a ref because the handler closes over every piece of the
   * table's local state, so its identity changes on each render. As a
   * dependency it would re-run this effect constantly; as a ref the effect
   * stays keyed to the item. */
  const onAutoSaveRef = useRef(onAutoSave);
  onAutoSaveRef.current = onAutoSave;

  useEffect(() => {
    if (!enabled || !assignedId) return;
    if (autoSavedAssignedIds.has(assignedId)) return;
    /** Claim the id before saving, not after. Two mounts of the same row can
     * land in one tick, and an await inside the handler would leave a window
     * for the second to slip through. */
    autoSavedAssignedIds.add(assignedId);
    onAutoSaveRef.current();
    /** onAutoSave is intentionally absent: it is read through a ref so this
     * fires once per assignedId, not once per handler identity. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedId, enabled]);
};

export default usePreSaveAutoSave;
